import { useEffect, useState } from "react";
import { RefreshCw, Clock, Phone, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { campaignService } from "../../services/campaignService";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { useToast } from "../ui/use-toast";
import { formatDistanceToNow } from "date-fns";

interface CallFlowQueueMonitorProps {
  callflowId: string;
}

export function CallFlowQueueMonitor({ callflowId }: CallFlowQueueMonitorProps) {
  const [queueData, setQueueData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const { toast } = useToast();

  const fetchQueueStatus = async () => {
    setLoading(true);
    try {
      const { data, error } = await campaignService.getCallFlowQueueStatus(callflowId);
      if (error) throw error;
      setQueueData(data);
    } catch (error) {
      console.error("Error fetching queue status:", error);
      toast({
        title: "Error",
        description: "Failed to load queue status",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueueStatus();
  }, [callflowId]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchQueueStatus();
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, callflowId]);

  if (!queueData) {
    return <div className="py-8 text-center">Loading queue status...</div>;
  }

  const { statistics, concurrency, pending_queue, active_calls, callflow_status } = queueData;

  const formatTimeUntil = (seconds: number) => {
    if (seconds <= 0) return "Ready now";
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (minutes > 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    }
    return minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline">Pending</Badge>;
      case "in-progress":
        return <Badge variant="default">In Progress</Badge>;
      case "completed":
        return <Badge variant="success">Completed</Badge>;
      case "failed":
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Queue Monitor</h2>
          <p className="text-sm text-gray-500">
            Real-time monitoring of CallFlow queue and worker activity
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? "animate-spin" : ""}`} />
            {autoRefresh ? "Auto" : "Manual"}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchQueueStatus} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Leads</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Ready to Call</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{statistics.ready_to_call}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Pending</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{statistics.pending}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>In Progress</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{statistics["in_progress"] || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Completed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{statistics.completed}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Failed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{statistics.failed}</div>
          </CardContent>
        </Card>
      </div>

      {/* Concurrency Status */}
      <Card>
        <CardHeader>
          <CardTitle>Concurrency Status</CardTitle>
          <CardDescription>Current call capacity usage</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Active Calls</span>
              <span className="text-lg font-bold">
                {concurrency.current} / {concurrency.max}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${(concurrency.current / concurrency.max) * 100}%` }}
              ></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Calls */}
      {active_calls && active_calls.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Phone className="h-5 w-5 mr-2" />
              Active Calls ({active_calls.length})
            </CardTitle>
            <CardDescription>Calls currently in progress</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {active_calls.map((call: any) => (
                <div
                  key={call.id}
                  className="flex items-center justify-between p-3 bg-blue-50 rounded-lg"
                >
                  <div>
                    <div className="font-medium">{call.lead_name}</div>
                    <div className="text-sm text-gray-500">
                      Phone: {call.phone_number} | Attempt {call.actual_attempts}
                    </div>
                  </div>
                  <Badge variant="default">Calling</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Queue */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            Pending Queue ({pending_queue.length})
          </CardTitle>
          <CardDescription>
            Leads waiting to be called (showing first 50)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pending_queue.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No pending leads</div>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {pending_queue.map((lead: any) => (
                <div
                  key={lead.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    lead.is_ready ? "bg-green-50 border border-green-200" : "bg-gray-50"
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <div className="font-medium">{lead.lead_name}</div>
                      {lead.is_ready && (
                        <Badge variant="success" className="text-xs">
                          Ready
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      Phone: {lead.phone_number} | Attempt {lead.actual_attempts + 1} of{" "}
                      {lead.max_attempts}
                    </div>
                    {lead.last_ended_reason && (
                      <div className="text-xs text-gray-400 mt-1">
                        Last reason: {lead.last_ended_reason}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">
                      {lead.is_ready ? (
                        <span className="text-green-600">Ready Now</span>
                      ) : (
                        <span className="text-yellow-600">
                          {formatTimeUntil(lead.time_until_call)}
                        </span>
                      )}
                    </div>
                    {lead.next_attempt_at && !lead.is_ready && (
                      <div className="text-xs text-gray-400">
                        {formatDistanceToNow(new Date(lead.next_attempt_at), {
                          addSuffix: true,
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardHeader>
          <CardTitle>Status Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-100 border border-green-200 rounded"></div>
              <span className="text-sm">Ready to call now</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-gray-100 rounded"></div>
              <span className="text-sm">Scheduled for later</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-100 rounded"></div>
              <span className="text-sm">Call in progress</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-sm">Completed</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
